import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { QUESTIONS, FRAGRANCES, computeResult } from "../config/content";
import { startSession, completeSession } from "../lib/api";
import { Intro } from "../components/Intro";
import { QuizStep } from "../components/QuizStep";
import { ResultScreen } from "../components/ResultScreen";
import { ShareCard } from "../components/ShareCard";
import { Logo } from "../components/Logo";

export default function Experience() {
  const [searchParams] = useSearchParams();
  const sourceRef = useRef(searchParams.get("source") || "diretto");
  const sessionIdRef = useRef(null);
  const [phase, setPhase] = useState("intro"); // intro | quiz | result
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [phase, step]);

  const begin = () => {
    setPhase("quiz");
    startSession(sourceRef.current)
      .then((res) => (sessionIdRef.current = res.data.session_id))
      .catch(() => {});
  };

  const answer = (letter) => {
    const q = QUESTIONS[step];
    const next = { ...answers, [String(q.id)]: letter };
    setAnswers(next);
    if (step < QUESTIONS.length - 1) {
      setStep(step + 1);
    } else {
      const r = computeResult(next);
      setResult(r.id);
      completeSession({
        session_id: sessionIdRef.current,
        answers: next,
        result: r.id,
        scores: r.scores,
        source: sourceRef.current,
      }).catch(() => {});
      setPhase("result");
    }
  };

  const restart = () => {
    setAnswers({});
    setStep(0);
    setResult(null);
    sessionIdRef.current = null;
    setPhase("quiz");
    startSession(sourceRef.current)
      .then((res) => (sessionIdRef.current = res.data.session_id))
      .catch(() => {});
  };

  const fragrance = FRAGRANCES.find((f) => f.id === result);

  return (
    <div className="min-h-screen bg-ivory">
      <AnimatePresence mode="wait">
        {phase === "intro" && (
          <motion.div key="intro" exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
            <Intro onStart={begin} />
          </motion.div>
        )}

        {phase === "quiz" && (
          <motion.div
            key="quiz"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            data-testid="quiz-screen"
            className="min-h-screen flex flex-col"
          >
            <header className="px-6 pt-8 pb-6 max-w-md mx-auto w-full">
              <div className="flex items-center justify-between">
                {step > 0 ? (
                  <button
                    onClick={() => setStep(step - 1)}
                    data-testid="back-button"
                    className="flex items-center gap-2 text-muted2 transition-colors hover:text-ink"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span className="font-mono text-[10px] tracking-[0.25em] uppercase">Indietro</span>
                  </button>
                ) : (
                  <Logo small />
                )}
                <span
                  data-testid="progress-indicator"
                  className="font-mono text-[11px] tracking-[0.3em] text-ink"
                >
                  {step + 1} / {QUESTIONS.length}
                </span>
              </div>
              <div className="mt-6 h-px bg-line">
                <motion.div
                  className="h-px bg-gold"
                  animate={{ width: `${((step + 1) / QUESTIONS.length) * 100}%` }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                />
              </div>
            </header>

            <main className="flex-1 flex flex-col justify-start pt-4 pb-16">
              <AnimatePresence mode="wait">
                <QuizStep key={QUESTIONS[step].id} question={QUESTIONS[step]} onAnswer={answer} />
              </AnimatePresence>
            </main>
          </motion.div>
        )}

        {phase === "result" && fragrance && (
          <motion.div key="result" exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
            <ResultScreen fragrance={fragrance} sessionId={sessionIdRef.current} onRestart={restart} onShare={() => setShareOpen(true)} />
          </motion.div>
        )}
      </AnimatePresence>

      {fragrance && (
        <ShareCard fragrance={fragrance} open={shareOpen} onClose={() => setShareOpen(false)} />
      )}
    </div>
  );
}
